import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '../../../../helpers'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { BillingConfig } from '../../../../models'
import { InternalInvoice } from '../../../../models'

export const usePropertyBilling = (propertyId: string) => {
    const queryClient = useQueryClient()
    const configQueryKey = ['property', propertyId, 'billing-config']
    const invoicesQueryKey = ['property', propertyId, 'invoices']

    // Fetch invoices
    const {
        data: invoices,
        isLoading: isLoadingInvoices,
        error: invoicesError,
    } = useQuery<AxiosResponse<InternalInvoice[]>, Error>({
        queryKey: invoicesQueryKey,
        queryFn: () =>
            axiosInstance.get(
                `account-manager/properties/${propertyId}/get-property-invoices/`,
            ),
        enabled: !!propertyId,
        staleTime: 5 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    // Update billing configuration
    const updateBillingConfig = useMutation({
        mutationFn: (newConfig: Partial<BillingConfig>) =>
            axiosInstance.post(
                `/account-manager/properties/${propertyId}/update-billing-info/`,
                newConfig,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: configQueryKey })
        },
        onError: (error: Error) => {
            toast.error(error.message)
        },
    })

    // Create new invoice
    const createInvoice = useMutation({
        mutationFn: (invoiceData: Partial<InternalInvoice>) =>
            axiosInstance.post(
                `/account-manager/properties/${propertyId}/invoices/`,
                invoiceData,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: invoicesQueryKey })
        },
    })

    // Update invoice status
    const updateInvoiceStatus = useMutation({
        mutationFn: ({
            invoiceId,
            status,
        }: {
            invoiceId: string
            status: InternalInvoice['status']
        }) =>
            axiosInstance.patch(
                `/account-manager/properties/${propertyId}/invoices/${invoiceId}/`,
                { status },
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: invoicesQueryKey })
        },
    })

    return {
        // Billing Config
        updateBillingConfig,

        // Invoices
        invoices: invoices?.data,
        isLoadingInvoices,
        invoicesError,
        createInvoice,
        updateInvoiceStatus,

        // Combined loading state
        isLoading: isLoadingInvoices,
    }
}
